import React, { useMemo, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { Icon, Div, Text } from '@pinpoint/design-kit';
import { checkHiddenByRole, checkSufficientRole } from 'src/functions';
import CONSTANTS from 'src/store/constants';
import useModels from 'src/hooks/swr/useModels';
import useScoringInProgress from 'src/hooks/useScoringInProgress';
import { CellTag, CellRegulatoryTag } from 'src/components/models/cells';
import Button from 'src/components/button';
import BaseDialog from './base';
/* eslint-disable react/prop-types */

const getDownloadDialogTitle = (tag, hasScoringInProgress) => {
  const action = hasScoringInProgress ? 'Scoring' : 'Download';

  if ([CONSTANTS.SEGMENT.TAG.VALIDATION, CONSTANTS.SEGMENT.TAG.ACTUARIAL, 'ACTVAL'].includes(tag)) {
    return `${action} validation predictions`;
  }

  if (tag === CONSTANTS.SEGMENT.TAG.TARGET) {
    return `${action} predictions`;
  }

  return 'Downloads';
};

export function DownloadDialog({ isOpen, actions, id: segmentId, downloads = [], statuses = [], tag, siteRole, role }) {
  const { hasScoringInProgress, scoringInProgressItems } = useScoringInProgress(segmentId, statuses);

  const combinedDownloads = useMemo(
    () => [...scoringInProgressItems, ...downloads],
    [scoringInProgressItems, downloads]
  );

  const modelsQuery = useMemo(() => {
    const query = {
      filters: [
        {
          name: 'id',
          op: 'in',
          val: [...combinedDownloads.map((dl) => dl?.metadataPayload?.source_id).filter((elem) => elem)],
        },
        { name: 'data_model_type', op: 'neq', val: CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL },
      ],
      order_by: [
        {
          field: 'name',
          direction: 'desc',
        },
      ],
      fields:
        'items{id,name,dataModelType,framework,predictionType,typeDetail,hiddenByRole,modelData{metadataPayload}},*',
    };
    return query;
  }, [combinedDownloads]);

  // query models to be able to match downloads against name for backwards compatibility when downloads does not contain dataModelType field
  const { data: modelList = [] } = useModels({
    query: modelsQuery,
    // @ts-ignore
    config: { revalidateOnMount: true, shouldFetch: combinedDownloads.length },
  });
  const allDownloads = useMemo(() => {
    let temp;
    if (hasScoringInProgress) {
      const currentScoringDownloads = scoringInProgressItems.map((current) => {
        const downloadBody = current?.payload?.future_download ?? {
          id: 'scoring-in-progress',
          name: 'Scoring in progress',
          dataModelType: null,
          dlHiddenByRole: null,
        };
        downloadBody.scoringInProgress = true;
        return downloadBody;
      });
      temp = [...currentScoringDownloads, ...downloads];
    } else {
      temp = downloads;
    }
    return temp.reduce((acc, cur) => {
      const model = modelList.find?.((mdl) => mdl?.id === cur?.metadataPayload?.source_id);
      if (!checkHiddenByRole(siteRole || role, model?.hiddenByRole))
        return [
          ...acc,
          { ...cur, framework: model?.framework, modelData: model?.modelData, dlHiddenByRole: cur?.hiddenByRole },
        ];
      return acc;
    }, []);
  }, [downloads, hasScoringInProgress, scoringInProgressItems, modelList, siteRole, role]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <BaseDialog
      isOpen={isOpen}
      modalWidth="clamp(40%, 900px, 100vw)"
      hideCloseIcon
      actions={{ close: actions.close, cancel: actions.cancel }}
      title={{
        text: getDownloadDialogTitle(tag, hasScoringInProgress),
        backgroundColor: 'brand300',
        color: 'white',
        textSize: 'title',
      }}
      items={{
        content: [
          // @ts-ignore
          ...allDownloads.map((dl, idx) => {
            const { name, id, dataModelType, scoringInProgress, modelData, framework, dlHiddenByRole } = dl;
            if (checkSufficientRole(siteRole || role, dlHiddenByRole, false))
              return (
                <Div
                  key={id}
                  d="flex"
                  flexDir="row"
                  justify="space-between"
                  align="center"
                  m={{ t: idx !== 0 ? '1.25rem' : '0' }}
                  data-test="download-row"
                >
                  {dataModelType && (
                    <>
                      <CellTag
                        value={{ dataModelType, isRemediated: modelData?.metadataPayload?.fairness ?? false }}
                        bg="white"
                        m={{ r: framework ? '.25rem' : '.75rem' }}
                      />
                      <CellRegulatoryTag value={{ framework }} m={{ r: '.75rem' }} />
                    </>
                  )}
                  <Text textSize="subtitle" textColor="tabSelected" maxW="calc(100% - 24rem)">
                    {name?.replace(`${CONSTANTS.TAL_MODEL.TAG_NAME[dataModelType]}:`, '').replace(' (REMEDIATED)', '')}
                  </Text>
                  <Div flexGrow="1" />
                  {!scoringInProgress && (
                    <>
                      <Button
                        buttonStyle="primary"
                        w="1.875rem"
                        onClick={() => actions.handlers[1].on(id, name)}
                        data-tip={`Delete "${name}"`}
                        data-test="delete-btn"
                      >
                        <Icon color="white" name="Delete" viewBox="0 0 50 50" size=".875rem" cursor="pointer" />
                      </Button>
                      {checkSufficientRole(siteRole, CONSTANTS.ROLE.CLIENT_SERVICES, true) && (
                        <Button
                          buttonStyle="primary"
                          w="1.875rem"
                          onClick={() => actions.handlers[2].on(id, name)}
                          data-tip={dlHiddenByRole ? 'Reveal Download' : 'Hide Download'}
                          data-test="visibility-btn"
                        >
                          <Icon
                            color="white"
                            name={dlHiddenByRole ? 'ClosedEye' : 'Visualize'}
                            viewBox={dlHiddenByRole ? '3 33 66 29' : undefined}
                            size=".875rem"
                            cursor="pointer"
                          />
                        </Button>
                      )}
                    </>
                  )}
                  {scoringInProgress ? (
                    <Button
                      buttonStyle="primary"
                      disabled="true"
                      m={{ r: '0rem' }}
                      data-tip="You'll receive an email when your download is ready"
                      data-test="Scoring in progress"
                      onClick={() => actions.handlers[0].on(id)}
                    >
                      <Icon
                        color="white"
                        name="InProgress"
                        viewBox="0 0 100 100"
                        size="1rem"
                        // cursor="pointer"
                        m={{ r: '1rem' }}
                      />
                      <Text textWeight="500">SCORING IN PROGRESS</Text>
                    </Button>
                  ) : (
                    <Button
                      buttonStyle="primary"
                      m={{ r: '0rem' }}
                      data-test={`Download "${name}"`}
                      onClick={() => actions.handlers[0].on(id)}
                    >
                      <Icon
                        color="white"
                        name="Download2"
                        viewBox="0 0 14 14"
                        size=".875rem"
                        cursor="pointer"
                        m={{ r: '1rem' }}
                      />
                      <Text textWeight="500">DOWNLOAD</Text>
                    </Button>
                  )}
                </Div>
              );
            return null;
          }),
          <Div key="last-spacer" data-test="last-spacer" h="1.875rem" m={{ t: '1.25rem' }} />,
        ],
      }}
    />
  );
}

/* eslint-disable react/require-default-props */
DownloadDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    cancel: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    close: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    handlers: PropTypes.arrayOf(
      PropTypes.shape({
        before: PropTypes.func,
        on: PropTypes.func.isRequired,
        after: PropTypes.func,
      })
    ),
  }).isRequired,
  title: PropTypes.shape({
    text: PropTypes.string.isRequired,
    textSize: PropTypes.string,
    backgroundColor: PropTypes.string.isRequired,
    color: PropTypes.string,
  }),
  items: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.element).isRequired,
    buttons: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        textSize: PropTypes.string,
        close: PropTypes.bool.isRequired,
      }).isRequired
    ),
  }).isRequired,
  downloads: PropTypes.arrayOf(PropTypes.shape({})),
  tag: PropTypes.string.isRequired,
  role: PropTypes.string.isRequired,
  siteRole: PropTypes.string.isRequired,
};
/* eslint-enable */

export default DownloadDialog;
